import { useMemo, useState } from "react";
import { useLocation } from "react-router";

import Header from "components/Header";
import Stepper from "components/Stepper";
import {
  CONNECT_EXISTING_PROFILE_STEP_LIST,
  INVITED_CARRIER_PROFILE_STEP_LIST,
  INVITE_CARRIER_STEP_LIST
} from "constants/haulpay-broker";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { IPaymentProfile } from "models/HaulPay";
import ConnectExistingProfile from "./ConnectExistingProfile";
import ConnectSuccess from "./ConnectSuccess";
import CreateProfile from "./CreateProfile";
import SetupPaymentSpeed from "./SetupPaymentSpeed";
import UploadDocumentsForNOA from "./UploadDocuments";

interface ISectionProps {
  step: number;
  type: "existing_profile" | "create_new_profile" | "invited_profile";
  profile: { email: string; phone_number: string };
  profilesData?: (IPaymentProfile & { is_existing: boolean })[];
  setStepCallback: (step: number) => void;
  selectedPaymentProfile?: IPaymentProfile;
  setSelectedPaymentProfile: React.Dispatch<React.SetStateAction<IPaymentProfile | undefined>>;
}

function SelectedFlow({
  step,
  type,
  profile,
  profilesData,
  setStepCallback,
  selectedPaymentProfile,
  setSelectedPaymentProfile
}: Readonly<ISectionProps>) {
  const stepByType = type === "create_new_profile" ? step + 1 : step;
  switch (stepByType) {
    case 0:
      return type === "invited_profile" ? (
        <CreateProfile
          profile={profile}
          handleNext={() => setStepCallback(step + 1)}
          setSelectedPaymentProfile={setSelectedPaymentProfile}
        />
      ) : (
        <ConnectExistingProfile
          handleNext={() => setStepCallback(step + 1)}
          profilesData={profilesData}
          selectedPaymentProfile={selectedPaymentProfile}
          setSelectedPaymentProfile={setSelectedPaymentProfile}
        />
      );

    case 1:
      return (
        <SetupPaymentSpeed
          type={type}
          paymentProfile={selectedPaymentProfile || profilesData?.[0]}
          handleNext={() => setStepCallback(step + 1)}
        />
      );

    case 2:
      return (
        <UploadDocumentsForNOA
          handleNext={() => setStepCallback(step + 1)}
          selectedPaymentProfile={selectedPaymentProfile}
        />
      );

    case 3:
      return <ConnectSuccess type={type} />;

    default:
      return null;
  }
}

export default function NewCarrierProfileFlow() {
  const { state } = useLocation();
  const { isDesktop } = useDeviceType();
  const [step, setStep] = useState(0);
  const [selectedPaymentProfile, setSelectedPaymentProfile] = useState<IPaymentProfile>();

  const stepperData = useMemo(() => {
    switch (state?.type as string) {
      case "existing_profile":
        return CONNECT_EXISTING_PROFILE_STEP_LIST;

      case "create_new_profile":
        return INVITE_CARRIER_STEP_LIST;

      case "invited_profile":
        return INVITED_CARRIER_PROFILE_STEP_LIST;

      default:
        return INVITE_CARRIER_STEP_LIST;
    }
  }, [state]);

  return (
    <MainLayout
      desktopTitle="My Carriers"
      customHeader={<Header label={isDesktop ? "Add a New Carrier" : "Add New"} />}
      className="px-5">
      <Stepper
        data={stepperData}
        currentStep={step}
        setStep={setStep}
        className="z-10 mb-7"
        containClass="desktop:!bg-extraCard"
        isHideMobileTitle
      />
      <SelectedFlow
        step={step}
        type={state?.type}
        profile={state?.profile ?? { email: "", phone_number: "" }}
        profilesData={state?.profiles}
        setStepCallback={setStep}
        selectedPaymentProfile={selectedPaymentProfile}
        setSelectedPaymentProfile={setSelectedPaymentProfile}
      />
    </MainLayout>
  );
}
